import React, { useState, useRef, useEffect } from "react";
import {
  Camera,
  Upload,
  Leaf,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Car,
  TreePine,
  Target,
  Calendar,
  Settings,
  BarChart,
  Home,
  ArrowRight,
  Smartphone,
  Brain,
  Globe,
  FileText, 
  Users, 
  Clock
} from "lucide-react";

const ReceiptCO2Calculator = () => {
  const [currentPage, setCurrentPage] = useState("home");
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState("");
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  // Default Railway URL - can be overridden with REACT_APP_API_URL
  const DEFAULT_API_URL =
    process.env.REACT_APP_API_URL ||
    "https://ecoreceiptbackend-production.up.railway.app";

  const [apiUrl, setApiUrl] = useState(DEFAULT_API_URL);
  const [apiStatus, setApiStatus] = useState("unknown");
  const fileInputRef = useRef(null);
  const processingRef = useRef(null);

  useEffect(() => {
    // Auto-load saved API URL from localStorage
    const savedUrl = localStorage.getItem("grocery-co2-api-url");
    if (savedUrl) {
      setApiUrl(savedUrl);
      checkApiStatus(savedUrl);
    }
  }, []);

  const checkApiStatus = async (url = apiUrl) => {
    if (!url) return;

    try {
      const response = await fetch(`${url}/health`);
      if (response.ok) {
        const data = await response.json();
        setApiStatus(data.openai_configured ? "ready" : "no-openai");
      } else {
        setApiStatus("offline");
      }
    } catch (error) {
      setApiStatus("offline");
    }
  };

  useEffect(() => {
    if (processing) {
      processingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [processing]);

  const handleApiUrlChange = (newUrl) => {
    setApiUrl(newUrl);
    localStorage.setItem("grocery-co2-api-url", newUrl);
    if (newUrl) {
      checkApiStatus(newUrl);
    }
  };

  const isPDF = (file) => {
    return (
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf")
    );
  };

  const processReceiptWithAPI = async (file) => {
    if (!apiUrl) {
      throw new Error("Please configure your Railway API URL first");
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("fileType", isPDF(file) ? "pdf" : "image");

      setProcessingStatus("Uploading to AI server...");
      setProcessingProgress(20);

      const response = await fetch(`${apiUrl}/api/process-receipt-ai`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API Error: ${response.status}`);
      }

      setProcessingStatus("AI analyzing receipt...");
      setProcessingProgress(70);

      const result = await response.json();

      setProcessingStatus("Processing complete!");
      setProcessingProgress(100);

      return result;
    } catch (error) {
      console.error("API processing error:", error);
      throw new Error(`Failed to process with AI: ${error.message}`);
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      processReceipt(selectedFile);
    }
  };

  const handleCameraCapture = () => {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute("capture", "camera");
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("border-green-400", "bg-green-50");
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("border-green-400", "bg-green-50");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("border-green-400", "bg-green-50");
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setFile(files[0]);
      processReceipt(files[0]);
    }
  };

  const processReceipt = async (receiptFile) => {
    setProcessing(true);
    setError(null);
    setProcessingProgress(0);
    setProcessingStatus("Starting AI analysis...");
    setResults(null);

    try {
      const apiResults = await processReceiptWithAPI(receiptFile);

      setProcessingStatus("Complete!");
      setProcessingProgress(100);

      setTimeout(() => {
        setResults(apiResults);
      }, 500);
    } catch (err) {
      console.error("Error processing receipt:", err);
      setError(
        err.message ||
          "Failed to process receipt. Please check your API configuration and try again."
      );
    } finally {
      setTimeout(() => {
        setProcessing(false);
        setProcessingProgress(0);
        setProcessingStatus("");
      }, 1000);
    }
  };

  const testSampleReceipt = async () => {
    if (!apiUrl) {
      setError("Please configure your Railway API URL first");
      return;
    }

    setProcessing(true);
    setError(null);
    setProcessingProgress(0);
    setProcessingStatus("Testing with sample data...");

    try {
      const sampleText = `K-Citymarket Helsinki
Naudan jauheliha 500g    12.90
Pirkka maito 1L         1.98
Banaani 1kg             2.50
Kurkku                  1.31
Porkkana 500g           1.39
YHTEENSÄ               20.08`;

      setProcessingProgress(50);
      setProcessingStatus("Processing sample receipt...");

      const response = await fetch(`${apiUrl}/api/process-text`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text: sampleText }),
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const result = await response.json();
      setProcessingProgress(100);
      setProcessingStatus("Sample processing complete!");

      setTimeout(() => {
        setResults(result);
      }, 500);
    } catch (err) {
      setError(`Sample test failed: ${err.message}`);
    } finally {
      setTimeout(() => {
        setProcessing(false);
        setProcessingProgress(0);
        setProcessingStatus("");
      }, 1000);
    }
  };

  const generateSuggestions = () => {
    if (!results || !results.categories) return [];

    const suggestions = [];

    for (const [category, data] of Object.entries(results.categories)) {
      if (category === "Red Meat" && data.emissions > 5) {
        suggestions.push({
          title: "Reduce Red Meat Consumption",
          description: `Your red meat purchases generated ${data.emissions.toFixed(
            1
          )} kg CO₂e. Switching to chicken or plant-based proteins could save up to 80% of these emissions.`,
          impact: `Potential savings: ${(data.emissions * 0.8).toFixed(
            1
          )} kg CO₂e`,
          icon: <TrendingUp className="w-5 h-5 text-green-600" />,
          priority: "high",
        });
      }

      if (category === "Dairy" && data.emissions > 3) {
        suggestions.push({
          title: "Try Plant-Based Alternatives",
          description: `Your dairy products generated ${data.emissions.toFixed(
            1
          )} kg CO₂e. Plant-based milk alternatives can reduce emissions by 60-80%.`,
          impact: `Potential savings: ${(data.emissions * 0.7).toFixed(
            1
          )} kg CO₂e`,
          icon: <Leaf className="w-5 h-5 text-green-600" />,
          priority: "medium",
        });
      }

      if (category === "Processed Food" && data.emissions > 2) {
        suggestions.push({
          title: "Choose Fresh Over Processed",
          description: `Processed foods generated ${data.emissions.toFixed(
            1
          )} kg CO₂e. Fresh ingredients typically have lower carbon footprints.`,
          impact: `Potential savings: ${(data.emissions * 0.4).toFixed(
            1
          )} kg CO₂e`,
          icon: <CheckCircle className="w-5 h-5 text-green-600" />,
          priority: "medium",
        });
      }
    }

    if (suggestions.length === 0) {
      suggestions.push({
        title: "Great Job!",
        description:
          "Your shopping choices show good environmental awareness. Keep up the sustainable shopping!",
        impact: "Continue making eco-friendly choices",
        icon: <CheckCircle className="w-5 h-5 text-green-600" />,
        priority: "low",
      });
    }

    return suggestions;
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Red Meat": "bg-red-500",
      Pork: "bg-pink-500",
      Poultry: "bg-orange-500",
      Fish: "bg-blue-500",
      Eggs: "bg-yellow-500",
      Dairy: "bg-purple-500",
      "Plant Proteins": "bg-green-500",
      Vegetables: "bg-green-400",
      Fruits: "bg-lime-500",
      Grains: "bg-amber-500",
      "Processed Food": "bg-gray-500",
      Beverages: "bg-cyan-500",
      Other: "bg-slate-500",
    };
    return colors[category] || "bg-gray-500";
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown";
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getApiStatusDisplay = () => {
    switch (apiStatus) {
      case "ready":
        return {
          text: "API Ready",
          color: "text-green-600",
          bg: "bg-green-100",
        };
      case "no-openai":
        return {
          text: "No OpenAI Key",
          color: "text-yellow-600",
          bg: "bg-yellow-100",
        };
      case "offline":
        return { text: "API Offline", color: "text-red-600", bg: "bg-red-100" };
      default:
        return { text: "Unknown", color: "text-gray-600", bg: "bg-gray-100" };
    }
  };

  const statusDisplay = getApiStatusDisplay();

  // Homepage Component
  const HomePage = () => {
  const [stats, setStats] = useState({
    total_receipts_processed: 0,
    receipts_today: 0,
    receipts_this_week: 0,
    average_emissions_per_receipt: 0,
    loading: true,
    error: null
  });

  // Fetch stats from API
  const fetchStats = async () => {
    try {
      const response = await fetch(`${apiUrl}/stats`); // Update with your actual API URL
      if (response.ok) {
        const data = await response.json();
        setStats({
          ...data,
          loading: false,
          error: null
        });
      } else {
        throw new Error('Failed to fetch stats');
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats(prev => ({
        ...prev,
        loading: false,
        error: 'Unable to load stats'
      }));
    }
  };

  // Load stats on component mount and refresh every 30 seconds
  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Format large numbers
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <div className="max-w-6xl mx-auto p-4">
        {/* Navigation */}
        <nav className="flex justify-between items-center mb-8 py-4">
          <div className="flex items-center">
            <Leaf className="w-8 h-8 text-green-600 mr-2" />
            <span className="text-xl font-bold text-gray-800">EcoReceipt</span>
          </div>
          <button
            onClick={() => window.open("https://netzerokoti.com/", "_blank")}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Visit Our Main Site
          </button>
        </nav>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="flex justify-center items-center mb-6">
            <Leaf className="w-20 h-20 text-green-600 mr-4" />
            <h1 className="text-5xl font-bold text-gray-800">
              EcoReceipt
            </h1>
          </div>
          <h2 className="text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Transform your grocery receipts into climate insights with AI-powered carbon footprint analysis
          </h2>
          
          {/* Live Stats Banner */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-green-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-800">Live Impact Statistics</h3>
            </div>
            
            {stats.loading ? (
              <div className="grid md:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="text-center">
                    <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse mx-auto mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded animate-pulse mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            ) : stats.error ? (
              <div className="text-center text-gray-500">
                <p>{stats.error}</p>
                <button 
                  onClick={fetchStats}
                  className="mt-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  Retry
                </button>
              </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="flex justify-center items-center mb-2">
                      <FileText className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="text-2xl font-bold text-gray-800">
                      {formatNumber(stats.total_receipts_processed)}
                    </div>
                    <p className="text-sm text-gray-600">Receipts Analyzed</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex justify-center items-center mb-2">
                      <Clock className="w-8 h-8 text-orange-600" />
                    </div>
                    <div className="text-2xl font-bold text-gray-800">
                      {stats.receipts_today}
                    </div>
                    <p className="text-sm text-gray-600">Today</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex justify-center items-center mb-2">
                      <Leaf className="w-8 h-8 text-purple-600" />
                    </div>
                    <div className="text-2xl font-bold text-gray-800">
                      {stats.average_emissions_per_receipt.toFixed(1)}kg
                    </div>
                    <p className="text-sm text-gray-600">Avg CO₂e</p>
                  </div>
                </div>
            )}
            
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                Join {formatNumber(Math.ceil(stats.total_receipts_processed / 3.2))} users tracking their food carbon footprint
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setCurrentPage("calculator")}
              className="flex items-center justify-center px-8 py-4 bg-green-600 text-white text-lg rounded-lg hover:bg-green-700 transition-colors"
            >
              <Camera className="w-6 h-6 mr-2" />
              Scan Your Receipt
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
            <button
              onClick={() => setCurrentPage("calculator")}
              className="flex items-center justify-center px-8 py-4 bg-blue-600 text-white text-lg rounded-lg hover:bg-blue-700 transition-colors"
            >
              <BarChart className="w-6 h-6 mr-2" />
              Try Demo
            </button>
          </div>
        </div>

        {/* Features Section */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="text-center p-6 bg-white rounded-xl shadow-lg">
            <Smartphone className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-3">
              Instant Scanning
            </h3>
            <p className="text-gray-600">
              Simply take a photo or upload your Finnish grocery receipt. Our OCR technology reads it instantly.
            </p>
          </div>
          
          <div className="text-center p-6 bg-white rounded-xl shadow-lg">
            <Brain className="w-16 h-16 text-purple-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-3">
              AI-Powered Analysis
            </h3>
            <p className="text-gray-600">
              Advanced AI categorizes your items and calculates precise CO₂ emissions using Finnish food data.
            </p>
          </div>
          
          <div className="text-center p-6 bg-white rounded-xl shadow-lg">
            <Globe className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-3">
              Actionable Insights
            </h3>
            <p className="text-gray-600">
              Get personalized suggestions to reduce your carbon footprint and make more sustainable choices.
            </p>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Camera className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">1. Upload</h3>
              <p className="text-gray-600 text-sm">
                Take a photo or upload your grocery receipt
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Brain className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">2. Analyze</h3>
              <p className="text-gray-600 text-sm">
                AI reads and categorizes your grocery items
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">3. Calculate</h3>
              <p className="text-gray-600 text-sm">
                Get precise CO₂ emissions for your purchases
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">4. Improve</h3>
              <p className="text-gray-600 text-sm">
                Receive personalized sustainability tips
              </p>
            </div>
          </div>
        </div>

        {/* Community Impact Section */}
        <div className="bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-xl shadow-lg p-8 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">Community Impact</h2>
            <p className="text-xl opacity-90">
              Together, we're making a difference
            </p>
          </div>
          
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white bg-opacity-20 rounded-lg p-6 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4" />
              <div className="text-4xl font-bold mb-2">
                {formatNumber(stats.total_receipts_processed)}
              </div>
              <p className="opacity-90">Receipts Analyzed</p>
            </div>
            
            <div className="bg-white bg-opacity-20 rounded-lg p-6 text-center">
              <Globe className="w-12 h-12 mx-auto mb-4" />
              <div className="text-4xl font-bold mb-2">
                {(stats.total_receipts_processed * stats.average_emissions_per_receipt / 1000).toFixed(1)}
              </div>
              <p className="opacity-90">Tonnes CO₂ Tracked</p>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <p className="text-lg opacity-90">
              Be part of Finland's grocery carbon tracking community
            </p>
          </div>
        </div>

        {/* Sample Results Preview */}
        <div className="bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-xl shadow-lg p-8 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">See Your Impact</h2>
            <p className="text-xl opacity-90">
              Example: Weekly grocery footprint analysis
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white bg-opacity-20 rounded-lg p-6 text-center">
              <div className="text-4xl font-bold mb-2">8.7 kg</div>
              <p className="opacity-90">CO₂ Emissions</p>
            </div>
            
            <div className="bg-white bg-opacity-20 rounded-lg p-6 text-center">
              <Car className="w-8 h-8 mx-auto mb-2" />
              <div className="text-2xl font-bold mb-2">72 km</div>
              <p className="opacity-90">Driving Equivalent</p>
            </div>
            
            <div className="bg-white bg-opacity-20 rounded-lg p-6 text-center">
              <TreePine className="w-8 h-8 mx-auto mb-2" />
              <div className="text-2xl font-bold mb-2">14 months</div>
              <p className="opacity-90">Tree Absorption</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Ready to Track Your Food Footprint?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join {formatNumber(Math.ceil(stats.total_receipts_processed / 3.2))} users making more sustainable grocery choices
          </p>
          <button
            onClick={() => setCurrentPage("calculator")}
            className="px-12 py-4 bg-green-600 text-white text-xl rounded-lg hover:bg-green-700 transition-colors"
          >
            Get Started Now
          </button>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-gray-900 text-white mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="flex justify-center items-center mb-6">
              <Home className="w-10 h-10 text-green-400 mr-4" />
              <span className="text-3xl font-bold">NetZeroKoti</span>
            </div>
            <p className="text-gray-300 mb-8 text-xl max-w-2xl mx-auto">
              Empowering Finnish households to achieve net-zero emissions through data-driven insights and actionable recommendations.
            </p>
            <div className="flex justify-center space-x-8 text-lg text-gray-400">
              <button 
                onClick={() => setCurrentPage("privacy")}
                className="hover:text-white transition-colors font-medium bg-transparent border-none text-lg cursor-pointer"
              >
                Privacy Policy
              </button>
              <a href="https://netzerokoti.com/" className="hover:text-white transition-colors font-medium">Contact</a>
              <a href="https://netzerokoti.com/" className="hover:text-white transition-colors font-medium">About</a>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-700">
              <p className="text-gray-500">© 2025 NetZeroKoti. All rights reserved.</p>
              <p className="text-gray-600 mt-2 text-sm">
                {formatNumber(stats.total_receipts_processed)} receipts analyzed • {formatNumber(Math.ceil(stats.total_receipts_processed / 3.2))} users • Live stats update every 30 seconds
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

  // Calculator Page Component  
  const CalculatorPage = () => {
  const [privacyConsent, setPrivacyConsent] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <div className="max-w-6xl mx-auto p-4">
        {/* Navigation */}
        <nav className="flex justify-between items-center mb-8 py-4">
          <button
            onClick={() => setCurrentPage("home")}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <Home className="w-5 h-5 mr-2" />
            Back to Home
          </button>
          <div className="flex items-center">
            <Leaf className="w-8 h-8 text-green-600 mr-2" />
            <span className="text-xl font-bold text-gray-800">EcoReceipt Calculator</span>
          </div>
        </nav>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-4">
            <Leaf className="w-12 h-12 text-green-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-800">
              Grocery CO₂ Calculator
            </h1>
          </div>
          <p className="text-lg text-gray-600">
            AI-powered Finnish receipt analysis for carbon footprint tracking
          </p>
          <div className="mt-4">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusDisplay.bg} ${statusDisplay.color}`}
            >
              <Settings className="w-4 h-4 mr-1" />
              {statusDisplay.text}
            </span>
          </div>
        </div>

        {/* API Configuration */}
        {/*!results && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <Target className="w-6 h-6 text-blue-600 mr-2" />
              API Configuration
            </h2>
            <div className="mb-4">
              <label
                htmlFor="apiUrl"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Railway API URL:
              </label>
              <div className="flex gap-2">
                <input
                  type="url"
                  id="apiUrl"
                  value={DEFAULT_API_URL}
                  onChange={(e) => handleApiUrlChange(e.target.value)}
                  placeholder={DEFAULT_API_URL}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={() => checkApiStatus()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Test
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Enter your Railway deployment URL to connect to the AI
                processing backend
              </p>
            </div>
          </div>
        )*/}

        {/* Upload Section (with inline privacy consent) */}
        {!results && (
          <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
            {/* Compact Privacy Consent Row */}
            <div className="flex items-start mb-6 pb-6 border-b border-gray-200">
              <input
                id="privacy-consent"
                type="checkbox"
                checked={privacyConsent}
                onChange={(e) => setPrivacyConsent(e.target.checked)}
                className="w-4 h-4 mt-0.5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 flex-shrink-0"
              />
              <label htmlFor="privacy-consent" className="ml-3 text-sm text-gray-700 cursor-pointer">
                🔒 I agree that EcoReceipt may process my receipt with AI to calculate a CO₂ score. The image/PDF isn't stored — only the results are.{" "}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentPage("privacy");
                  }}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Privacy Policy
                </button>
              </label>
            </div>

            <div className="text-center">
              <div
                className={`border-3 border-dashed rounded-lg p-8 mb-6 transition-colors cursor-pointer ${
                  privacyConsent 
                    ? "border-green-300 hover:border-green-400" 
                    : "border-gray-300 cursor-not-allowed opacity-50"
                }`}
                onDragOver={privacyConsent ? handleDragOver : undefined}
                onDragLeave={privacyConsent ? handleDragLeave : undefined}
                onDrop={privacyConsent ? handleDrop : undefined}
                onClick={privacyConsent ? () => fileInputRef.current?.click() : undefined}
              >
                <Camera className={`w-16 h-16 mx-auto mb-4 ${
                  privacyConsent ? "text-green-500" : "text-gray-400"
                }`} />
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                  Upload Your Receipt
                </h2>
                <p className="text-gray-600 mb-6">
                  {privacyConsent
                    ? "Drag & drop, take a photo, or upload an image/PDF of your Finnish grocery receipt"
                    : "Please check the box above to enable file upload"
                  }
                </p>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={!privacyConsent}
                />

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (privacyConsent) {
                        fileInputRef.current?.click();
                      }
                    }}
                    disabled={!privacyConsent}
                    className={`flex items-center justify-center px-6 py-3 rounded-lg transition-colors ${
                      privacyConsent
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    Choose File
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (privacyConsent) {
                        handleCameraCapture();
                      }
                    }}
                    disabled={!privacyConsent}
                    className={`flex items-center justify-center px-6 py-3 rounded-lg transition-colors ${
                      privacyConsent
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    Take Photo
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      testSampleReceipt();
                    }}
                    className="flex items-center justify-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <BarChart className="w-5 h-5 mr-2" />
                    Try Sample
                  </button>
                </div>
              </div>
              
              <p className="text-sm text-gray-500 mt-4">
                "Try Sample" works without file upload and doesn't require consent
              </p>
            </div>
          </div>
        )}

        {/* Processing Indicator */}
        {processing && (
          <div ref={processingRef} className="bg-white rounded-xl shadow-lg p-8 mb-6 text-center">
            <div className="relative w-24 h-24 mx-auto mb-4">
              <div className="absolute inset-0 border-4 border-green-200 rounded-full"></div>
              <div
                className="absolute inset-0 border-4 border-green-500 border-t-transparent rounded-full animate-spin"
                style={{ animationDuration: "1s" }}
              ></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-semibold text-green-600">
                  {processingProgress}%
                </span>
              </div>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <div
                className="bg-green-500 h-3 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${processingProgress}%` }}
              ></div>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Analyzing your receipt with AI...
            </h3>
            <p className="text-gray-600">
              {processingStatus || "Processing with advanced AI analysis"}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              This usually takes 10-30 seconds
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">Error</h3>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {results && (
          <div className="space-y-6">
            {/* Store Info & Metadata */}
            {(results.store_info || results.metadata) && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      {results.store_info?.name || "Grocery Store Receipt"}
                    </h3>
                    <p className="text-gray-600">
                      Processed on {formatDate(results.metadata?.processed_at)}
                    </p>
                    {results.metadata?.processing_method && (
                      <p className="text-sm text-blue-600">
                        Method:{" "}
                        {results.metadata.processing_method.replace("_", " ")}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Total Spent</p>
                    <p className="text-xl font-bold text-gray-800">
                      €
                      {(
                        results.store_info?.total ||
                        results.total_price ||
                        0
                      ).toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {results.metadata?.items_found ||
                        results.items?.length ||
                        0}{" "}
                      items
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Summary Card */}
            <div className="bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-xl shadow-lg p-8 text-center">
              <h2 className="text-2xl font-semibold mb-4">
                Your Grocery Carbon Footprint
              </h2>
              <div className="text-5xl font-bold mb-4">
                {(results.total_emissions || 0).toFixed(1)} kg CO₂e
              </div>

              {/* Environmental Comparisons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <Car className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm opacity-90">Equivalent to driving</p>
                  <p className="text-xl font-bold">
                    {results.summary?.driving_equivalent_km ||
                      Math.round((results.total_emissions || 0) / 0.12)}{" "}
                    km
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <TreePine className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm opacity-90">Tree absorption time</p>
                  <p className="text-xl font-bold">
                    {results.summary?.tree_months_absorption ||
                      Math.round((results.total_emissions || 0) / 0.02)}{" "}
                    months
                  </p>
                </div>
              </div>
            </div>

            {/* Categories Breakdown */}
            {results.categories &&
              Object.keys(results.categories).length > 0 && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                    <TrendingUp className="w-6 h-6 text-green-600 mr-2" />
                    Emissions by Category
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(results.categories)
                      .sort(([, a], [, b]) => b.emissions - a.emissions)
                      .map(([category, data]) => (
                        <div
                          key={category}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center">
                            <div
                              className={`w-4 h-4 rounded-full ${getCategoryColor(
                                category
                              )} mr-3`}
                            ></div>
                            <div>
                              <span className="font-medium text-gray-800">
                                {category}
                              </span>
                              <span className="text-sm text-gray-600 ml-2">
                                ({data.count} items)
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-red-600">
                              {data.emissions.toFixed(2)} kg CO₂e
                            </div>
                            <div className="text-sm text-gray-600">
                              €{data.price.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

            {/* Items List */}
            {results.items && results.items.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Items Breakdown ({results.items.length} items)
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {results.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 hover:bg-gray-50 rounded"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-800">
                          {item.name}
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-block px-2 py-1 text-xs text-white rounded ${getCategoryColor(
                              item.category
                            )}`}
                          >
                            {item.category}
                          </span>
                          <span className="text-sm text-gray-600">
                            {(item.weight || 0).toFixed(2)} kg
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-red-600">
                          {(item.emissions || 0).toFixed(2)} kg CO₂e
                        </div>
                        <div className="text-sm text-gray-600">
                          €{(item.price || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Suggestions */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-green-800 mb-4 flex items-center">
                <Leaf className="w-6 h-6 mr-2" />
                Ways to Reduce Your Impact
              </h3>
              <div className="space-y-4">
                {generateSuggestions().map((suggestion, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg p-4 border border-green-200"
                  >
                    <div className="flex items-start">
                      {suggestion.icon}
                      <div className="ml-3">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-800">
                            {suggestion.title}
                          </h4>
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              suggestion.priority === "high"
                                ? "bg-red-100 text-red-700"
                                : suggestion.priority === "medium"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-green-100 text-green-700"
                            }`}
                          >
                            {suggestion.priority}
                          </span>
                        </div>
                        <p className="text-gray-600 mt-1">
                          {suggestion.description}
                        </p>
                        <p className="font-medium text-green-700 mt-2">
                          {suggestion.impact}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reset Button */}
            <div className="text-center">
              <button
                onClick={() => {
                  setResults(null);
                  setFile(null);
                  setError(null);
                }}
                className="px-8 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors mr-4"
              >
                Scan Another Receipt
              </button>
              <button
                onClick={() => setCurrentPage("home")}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Back to Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Privacy Policy Page Component
const PrivacyPolicyPage = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
    <div className="max-w-4xl mx-auto p-4">
      {/* Navigation */}
      <nav className="flex justify-between items-center mb-8 py-4">
        <button
          onClick={() => setCurrentPage("home")}
          className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <Home className="w-5 h-5 mr-2" />
          Back to Home
        </button>
        <div className="flex items-center">
          <Leaf className="w-8 h-8 text-green-600 mr-2" />
          <span className="text-xl font-bold text-gray-800">EcoReceipt Privacy Policy</span>
        </div>
      </nav>

      {/* Privacy Policy Content */}
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Settings className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Privacy Policy</h1>
          <h2 className="text-2xl font-semibold text-blue-600">EcoReceipt</h2>
          <p className="text-gray-600 mt-4">
            <strong>Effective Date:</strong> January 15, 2025
          </p>
        </div>

        <div className="prose prose-lg max-w-none text-gray-700">
          <div className="mb-8">
            <p className="text-lg leading-relaxed">
              At <strong>EcoReceipt</strong>, we respect your privacy and are committed to protecting your personal data. 
              This policy explains how we handle the receipts you upload to our service.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <FileText className="w-6 h-6 text-green-600 mr-2" />
              What Data We Process
            </h3>
            <p className="mb-4">When you upload a receipt (photo or PDF) to EcoReceipt:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>We process the file to extract and classify each item for CO₂ scoring.</li>
              <li>We store only:
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>The classification result for each item.</li>
                  <li>The calculated CO₂ score for the receipt (NetZero score).</li>
                  <li>The store name from the receipt.</li>
                </ul>
              </li>
              <li>We do <strong>not</strong> store the uploaded image or PDF — it is deleted immediately after processing.</li>
              <li>We do <strong>not</strong> collect any other personal information from your receipt.</li>
            </ul>
          </div>

          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <Brain className="w-6 h-6 text-purple-600 mr-2" />
              AI Processing Disclosure
            </h3>
            <p>
              EcoReceipt uses artificial intelligence (AI) to automatically recognize and classify items on your receipt 
              in order to calculate the CO₂ score. The AI processing is fully automated and operates only on the data 
              you choose to provide.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <Target className="w-6 h-6 text-blue-600 mr-2" />
              How We Use the Data
            </h3>
            <p className="mb-4">We use the processed data to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide you with the CO₂ score of your purchases.</li>
              <li>Improve our classification models and accuracy (using only anonymized data).</li>
            </ul>
          </div>

          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center mr-2">
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
              Data Security
            </h3>
            <p>
              We take reasonable technical and organizational measures to protect your data from unauthorized access, 
              use, or disclosure.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <Globe className="w-6 h-6 text-indigo-600 mr-2" />
              Your Rights under GDPR
            </h3>
            <p className="mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Request access to the data we store about you.</li>
              <li>Request correction or deletion of your stored receipt data.</li>
              <li>Withdraw your consent at any time.</li>
            </ul>
            <p className="mt-4">
              To exercise your rights, contact us at: 
              <a href="mailto:netzerokoti@gmail.com" className="text-blue-600 hover:text-blue-800 font-medium ml-1">
                netzerokoti@gmail.com
              </a>
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-2">
                <span className="text-white text-xs font-bold">@</span>
              </div>
              Contact
            </h3>
            <p className="mb-4">If you have questions about this policy, you can reach us at:</p>
            <div className="space-y-2">
              <p>
                <strong>Email:</strong> 
                <a href="mailto:netzerokoti@gmail.com" className="text-blue-600 hover:text-blue-800 font-medium ml-1">
                  netzerokoti@gmail.com
                </a>
              </p>
              <p>
                <strong>Website:</strong> 
                <a href="https://netzerokoti.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-medium ml-1">
                  https://netzerokoti.com
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Back to Home Button */}
        <div className="text-center mt-8">
          <button
            onClick={() => setCurrentPage("home")}
            className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  </div>
);

  // Main App Component
  return (
    <div>
      {currentPage === "home" && <HomePage />}
      {currentPage === "calculator" && <CalculatorPage />}
      {currentPage === "privacy" && <PrivacyPolicyPage />}
    </div>
  );
};

export default ReceiptCO2Calculator;
